import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../context/NotificationContext';
import './UserManagement.css'; 
import CompSetFormModal from './CompSetFormModal';
import ConfirmModal from './ConfirmModal';
import HotelAssignmentModal from './HotelAssignmentModal';

function CompSetManagement() {
    const [compSets, setCompSets] = useState([]);
    const [allHotels, setAllHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingCompSet, setEditingCompSet] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [compSetToDelete, setCompSetToDelete] = useState(null);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [managingCompSet, setManagingCompSet] = useState(null);
    const { showNotification } = useNotification();

    const getAuthHeader = useCallback(() => {
        const token = localStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }, []);

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const compsetsApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets`;
            const hotelsApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=hotels&limit=999`;

            const [compSetsResponse, hotelsResponse] = await Promise.all([
                fetch(compsetsApiUrl, { headers: getAuthHeader() }),
                fetch(hotelsApiUrl, { headers: getAuthHeader() })
            ]);

            if (!compSetsResponse.ok) throw new Error(`Error fetching comp sets: ${compSetsResponse.statusText}`);
            if (!hotelsResponse.ok) throw new Error(`Error fetching hotels: ${hotelsResponse.statusText}`);
            
            const compSetsData = await compSetsResponse.json();
            const hotelsData = await hotelsResponse.json();

            if (compSetsData.success && Array.isArray(compSetsData.data)) { 
                setCompSets(compSetsData.data); 
            } else { 
                throw new Error(compSetsData.error?.message || 'Error loading comp sets.'); 
            }

            if (hotelsData.success && hotelsData.data?.hotels) {
                setAllHotels(hotelsData.data.hotels);
            } else {
                throw new Error(hotelsData.error?.message || 'Error loading hotels.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleAddClick = () => { setEditingCompSet(null); setIsFormModalOpen(true); };
    const handleEditClick = (compSet) => { setEditingCompSet(compSet); setIsFormModalOpen(true); };
    const handleCompSetSaved = () => { fetchInitialData(); };
    const handleManageHotelsClick = (compSet) => { setManagingCompSet(compSet); setIsAssignmentModalOpen(true); };
    const handleDeleteRequest = (compSet) => { setCompSetToDelete(compSet); setIsConfirmModalOpen(true); };
    
    const handleConfirmDelete = async () => {
        if (!compSetToDelete) return;
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets/${compSetToDelete.comp_set_id}`;
            const response = await fetch(apiUrl, {
                method: 'DELETE',
                headers: getAuthHeader()
            });

            const result = await response.json();
            if (response.ok && result.success) {
                showNotification(result.data.message || 'Comp set eliminado!', 'success');
                fetchInitialData();
            } else {
                throw new Error(result.error?.message || 'Error desconocido');
            }
        } catch (err) {
            showNotification(`Error al eliminar: ${err.message}`, 'error');
        } finally {
            setIsConfirmModalOpen(false);
            setCompSetToDelete(null);
        }
    };

    return (
        <>
            <div className="user-management-container">
                <header className="main-header">
                    <h1 className="page-title">Administración de Comp Sets</h1>
                </header>
                <section className="info-entry-section">
                    <div className="user-list-header">
                        <h2 className="report-subtitle">Grupos Competitivos</h2>
                        <button className="add-user-btn" onClick={handleAddClick}>+ Agregar Comp Set</button>
                    </div>

                    {loading && <p>Cargando...</p>}
                    {error && <p className="error-message">{error}</p>}
                    
                    {!loading && !error && (
                        <div className="data-table-container">
                            <table className="data-input-table">
                                <thead>
                                    <tr>
                                        <th>Nombre del Comp Set</th>
                                        <th>Descripción</th>
                                        <th style={{ width: '350px' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {compSets.map(set => (
                                        <tr key={set.comp_set_id}>
                                            <td>{set.name}</td>
                                            <td>{set.description}</td>
                                            <td className="action-buttons">
                                                <button className="assign-btn" onClick={() => handleManageHotelsClick(set)}>Administrar Hoteles</button>
                                                <button className="edit-btn" onClick={() => handleEditClick(set)}>Editar Nombre</button>
                                                <button className="delete-btn" onClick={() => handleDeleteRequest(set)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
            <CompSetFormModal 
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onCompSetSaved={handleCompSetSaved}
                editingCompSet={editingCompSet}
            />
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación"
            >
                ¿Está seguro de que desea eliminar el comp set "{compSetToDelete?.name}"?
            </ConfirmModal>
            <HotelAssignmentModal 
                isOpen={isAssignmentModalOpen}
                onClose={() => setIsAssignmentModalOpen(false)}
                onSave={handleCompSetSaved}
                compSet={managingCompSet}
                allHotels={allHotels} 
            />
        </>
    );
}

export default CompSetManagement;